import React from 'react';
import { Calendar, Clock, Award, Play, AlertTriangle } from 'lucide-react';
import { TestResult } from '../types/testResult';

interface TestResultCardProps {
  result: TestResult;
  onView: () => void;
  detailed?: boolean;
}

/**
 * Test Result Card Component
 */
export const TestResultCard: React.FC<TestResultCardProps> = ({
  result,
  onView,
  detailed = false
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'COMPLETED': { color: 'bg-green-100 text-green-800', label: 'Completed' },
      'SUBMITTED': { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      'IN_PROGRESS': { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
      'expired': { color: 'bg-orange-100 text-orange-800', label: 'Expired' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['COMPLETED'];

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Test Title and Subject */}
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {result.testTitle || 'Untitled Test'}
              {result.attemptNumber && (
                <span className="ml-2 text-sm font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  Attempt #{result.attemptNumber}
                </span>
              )}
            </h3>
            {getStatusBadge(result.status)}
          </div>

          {/* Date and Duration */}
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" aria-hidden="true" />{formatDate(result.completedAt)}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{formatDuration(result.durationMinutes)}</span>
            {result.rank && (
              <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" aria-hidden="true" />Rank #{result.rank}</span>
            )}
          </div>

          {/* Score Display */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Marks:</span>
                <span className="font-semibold text-gray-900">
                  {result.score}/{result.maxScore}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Percentage:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(result.percentage)}`}>
                  {result.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            
            {/* Question Breakdown */}
            <div className="flex items-center flex-wrap gap-3 text-xs">
              <div className="flex items-center space-x-1">
                <span className="text-gray-600">Total:</span>
                <span className="font-medium text-gray-900">{result.totalQuestions}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-green-600">Correct:</span>
                <span className="font-medium text-green-700">{result.correctAnswers}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-red-600">Wrong:</span>
                <span className="font-medium text-red-700">{result.wrongAnswers}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-amber-600">Unanswered:</span>
                <span className="font-medium text-amber-700">{result.unansweredQuestions}</span>
              </div>
            </div>
          </div>

          {/* Detailed Information */}
          {detailed && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">{result.correctAnswers}</div>
                <div className="text-xs text-gray-500">Correct</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-red-600">{result.wrongAnswers}</div>
                <div className="text-xs text-gray-500">Wrong</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-600">{result.unansweredQuestions}</div>
                <div className="text-xs text-gray-500">Unanswered</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">
                  {result.percentile ? result.percentile.toFixed(1) : 'N/A'}
                </div>
                <div className="text-xs text-gray-500">Percentile</div>
              </div>
            </div>
          )}

          {/* Subject Results Preview */}
          {detailed && result.subjectResults && result.subjectResults.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Subject Performance:</h4>
              <div className="flex flex-wrap gap-2">
                {result.subjectResults.slice(0, 3).map((subject) => (
                  <div key={subject.subject} className="flex items-center space-x-1 text-xs">
                    <span className="text-gray-600">{subject.subject}:</span>
                    <span className={`px-2 py-1 rounded ${getScoreColor(subject.percentage)}`}>
                      {subject.percentage.toFixed(0)}%
                    </span>
                  </div>
                ))}
                {result.subjectResults.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{result.subjectResults.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Performance Indicators */}
          {result.averageScore && (
            <div className="flex items-center space-x-4 text-sm">
              <span className="text-gray-600">vs Average:</span>
              <span className={`font-medium ${
                result.scoreAboveAverage && result.scoreAboveAverage > 0
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}>
                {result.scoreAboveAverage && result.scoreAboveAverage > 0 ? '+' : ''}
                {result.scoreAboveAverage?.toFixed(1)} points
              </span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="ml-4">
          {result.status === 'IN_PROGRESS' ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={onView}
                className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                <span className="flex items-center"><Play className="w-4 h-4 mr-1.5" aria-hidden="true" />Resume Test</span>
              </button>
              <p className="text-xs text-gray-500 text-center">Test not yet submitted</p>
            </div>
          ) : (
            <button
              onClick={onView}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Details
            </button>
          )}
        </div>
      </div>

      {/* Banner for in-progress tests */}
      {result.status === 'IN_PROGRESS' && (
        <div className="mt-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
            This test is in progress and has not been submitted yet. Resume to complete or submit your answers.
          </p>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Progress</span>
          <span>{result.percentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              result.percentage >= 80
                ? 'bg-green-500'
                : result.percentage >= 60
                ? 'bg-yellow-500'
                : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(result.percentage, 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};
